import { useState, useEffect } from "react";
import { GripVertical, X, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { api, mediaUrl } from "../../lib/api";
import ImageCropper from "./ImageCropper";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";

export const Btn = ({ children, variant = "outline", className = "", ...props }) => {
  const base = "text-xs tracking-widest uppercase px-5 py-2.5 transition-colors duration-300 disabled:opacity-40";
  const styles = {
    outline: "border border-[#292929] hover:border-white",
    solid: "bg-white text-black hover:bg-[#dddddd]",
    ghost: "text-muted-eff hover:text-white",
    danger: "border border-[#292929] text-muted-eff hover:border-white hover:text-white",
  };
  return <button className={`${base} ${styles[variant]} ${className}`} {...props}>{children}</button>;
};

export const Label = ({ children }) => (
  <label className="block text-[10px] tracking-widest uppercase text-muted-eff mb-2">{children}</label>
);

const inputCls = "w-full bg-[#050505] border border-[#292929] px-3 py-2.5 text-sm focus:outline-none focus:border-white transition-colors duration-300";

export const TextField = ({ label, value, onChange, textarea, type = "text", placeholder = "" }) => (
  <div>
    {label && <Label>{label}</Label>}
    {textarea ? (
      <textarea className={`${inputCls} resize-none`} rows={3} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input className={inputCls} type={type} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)} />
    )}
  </div>
);

export const EffSelect = ({ value, onChange, options, placeholder = "Choisir", className = "", testid, size = "md" }) => (
  <Select value={value == null || value === "" ? undefined : String(value)} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}
      className={`bg-[#050505] border-[#292929] rounded-none uppercase tracking-widest text-[10px] focus:ring-0 focus:border-white data-[placeholder]:text-[#5a5a5a] ${size === "sm" ? "h-8 py-1 px-2" : "h-auto py-2.5 px-3 text-xs"} ${className}`}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="bg-[#080808] border-[#292929] text-[#f7f7f5] rounded-none z-[70]">
      {(options || []).map((o) => (
        <SelectItem key={o.value} value={o.value}
          className="uppercase tracking-widest text-[10px] rounded-none focus:bg-[#141414] focus:text-white cursor-pointer">
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const SelectField = ({ label, value, onChange, options }) => (
  <div>
    {label && <Label>{label}</Label>}
    <EffSelect value={value} onChange={onChange} options={options} />
  </div>
);

export const TrackPicker = ({ label, value, onChange }) => {
  const [tracks, setTracks] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  useEffect(() => { api.get("/admin/content/audio_tracks").then(({ data }) => setTracks(Array.isArray(data) ? data : [])).catch(() => setTracks([])); }, []);
  const ids = Array.isArray(value) ? value : (value ? String(value).split(",").map((s) => s.trim()).filter(Boolean) : []);
  const byId = (id) => tracks.find((t) => t.id === id);
  const selected = ids.map(byId).filter(Boolean);
  const available = tracks.filter((t) => !ids.includes(t.id));
  const add = (id) => onChange([...ids, id]);
  const removeId = (id) => onChange(ids.filter((x) => x !== id));
  const reorder = (from, to) => {
    if (from == null || to == null || from === to) return;
    const arr = [...ids];
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    onChange(arr);
  };
  const move = (i, d) => reorder(i, i + d);
  return (
    <div>
      {label && <Label>{label} · {ids.length} sélectionnée(s)</Label>}
      <div className="border border-[#292929]">
        <div className="px-3 py-2 text-[10px] tracking-widest uppercase text-muted-eff border-b border-[#292929]">Sélection · glissez pour réordonner</div>
        {selected.length === 0 && <div className="p-4 text-[11px] text-muted-eff">Aucune piste sélectionnée.</div>}
        {selected.map((t, i) => (
          <div key={t.id} data-testid={`track-selected-${t.id}`} draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { reorder(dragIdx, i); setDragIdx(null); }}
            className={`flex items-center gap-3 p-2.5 border-b border-[#161616] bg-[#0d0d0d] ${dragIdx === i ? "opacity-40" : ""}`}>
            <GripVertical className="w-4 h-4 text-muted-eff cursor-grab shrink-0" />
            <span className="w-6 h-6 shrink-0 flex items-center justify-center text-[10px] bg-white text-black">{i + 1}</span>
            {t.cover ? <img src={mediaUrl(t.cover)} alt="" className="w-9 h-9 object-cover border border-[#292929]" /> : <span className="w-9 h-9 border border-[#292929]" />}
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] truncate">{text(t.title)}</span>
              <span className="block text-[11px] text-muted-eff truncate">{text(t.artist)}</span>
            </span>
            <button type="button" data-testid={`track-up-${t.id}`} onClick={() => move(i, -1)} className="text-muted-eff hover:text-white"><ArrowUp className="w-4 h-4" /></button>
            <button type="button" data-testid={`track-down-${t.id}`} onClick={() => move(i, 1)} className="text-muted-eff hover:text-white"><ArrowDown className="w-4 h-4" /></button>
            <button type="button" data-testid={`track-remove-${t.id}`} onClick={() => removeId(t.id)} className="text-muted-eff hover:text-white"><X className="w-4 h-4" /></button>
          </div>
        ))}
        <div className="px-3 py-2 text-[10px] tracking-widest uppercase text-muted-eff border-b border-t border-[#292929]">Ajouter une piste</div>
        <div className="max-h-52 overflow-y-auto">
          {available.length === 0 && <div className="p-4 text-[11px] text-muted-eff">Toutes les pistes sont ajoutées.</div>}
          {available.map((t) => (
            <button type="button" key={t.id} data-testid={`track-pick-${t.id}`} onClick={() => add(t.id)}
              className="w-full flex items-center gap-3 p-2.5 text-left hover:bg-[#0a0a0a] border-b border-[#161616]">
              <Plus className="w-4 h-4 text-muted-eff shrink-0" />
              {t.cover ? <img src={mediaUrl(t.cover)} alt="" className="w-9 h-9 object-cover border border-[#292929]" /> : <span className="w-9 h-9 border border-[#292929]" />}
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] truncate">{text(t.title)}</span>
                <span className="block text-[11px] text-muted-eff truncate">{text(t.artist)} · {t.source_type === "embed" ? "Embed" : "Owned"}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const LIMITS = { image: "JPG/PNG/WEBP — 15 Mo max", cover: "JPG/PNG/WEBP — 15 Mo max", video: "MP4/WEBM — 300 Mo max", audio: "MP3/WAV — 60 Mo max", logo: "SVG/PNG — 5 Mo max" };

export const FileUpload = ({ label, value, onChange, kind = "image" }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [cropSrc, setCropSrc] = useState(null);
  const [cropName, setCropName] = useState("image.jpg");
  const isImg = kind === "image" || kind === "cover" || kind === "logo";
  const canCrop = kind === "image" || kind === "cover";

  const doUpload = async (blob, filename) => {
    setBusy(true); setErr("");
    const fd = new FormData();
    const f = blob instanceof File ? blob : new File([blob], filename, { type: blob.type || "image/jpeg" });
    fd.append("file", f, filename || f.name);
    fd.append("type", kind);
    try {
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      setCropSrc(null);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Echec de l'upload");
    } finally { setBusy(false); }
  };

  const onSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (canCrop && file.type !== "image/svg+xml") {
      const reader = new FileReader();
      reader.onload = () => { setCropName(file.name || "image.jpg"); setCropSrc(reader.result); };
      reader.readAsDataURL(file);
    } else {
      doUpload(file, file.name);
    }
  };

  const recropExisting = () => {
    if (!value) return;
    setCropName("recrop.jpg");
    setCropSrc(mediaUrl(value));
  };

  const handleCropped = (blob) => {
    if (!blob) { setErr("Recadrage impossible pour cette image"); setCropSrc(null); return; }
    doUpload(blob, `crop-${Date.now()}.${/\.png$/i.test(cropName) ? "png" : "jpg"}`);
  };

  const canRecrop = canCrop && value && String(value).startsWith("/api");

  return (
    <div>
      {label && <Label>{label}</Label>}
      <div className="flex items-start gap-4">
        {value && isImg && <img src={mediaUrl(value)} alt="" className="w-16 h-16 object-cover border border-[#292929]" />}
        <div className="flex-1">
          <input className={inputCls} value={value ?? ""} placeholder="URL ou upload"
            onChange={(e) => onChange(e.target.value)} />
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <label className="text-[10px] tracking-widest uppercase border border-[#292929] px-3 py-1.5 cursor-pointer hover:border-white transition-colors">
              {busy ? "..." : "Upload"}
              <input type="file" className="hidden" onChange={onSelect}
                accept={kind === "video" ? "video/*" : kind === "audio" ? "audio/*" : "image/*"} />
            </label>
            {canRecrop && (
              <button type="button" data-testid="recrop-btn" onClick={recropExisting}
                className="text-[10px] tracking-widest uppercase border border-[#292929] px-3 py-1.5 hover:border-white transition-colors">
                Recadrer
              </button>
            )}
            <span className="text-[10px] text-muted-eff">{LIMITS[kind]}</span>
          </div>
          {err && <div className="text-[10px] text-white mt-1">{err}</div>}
        </div>
      </div>
      {cropSrc && (
        <ImageCropper src={cropSrc} filename={cropName} busy={busy}
          onCancel={() => setCropSrc(null)} onCropped={handleCropped} />
      )}
    </div>
  );
};

export const text = (v) => (v == null ? "" : (typeof v === "object" ? JSON.stringify(v) : String(v)));

export const StatusBadge = ({ status }) => {
  const map = { PUBLISHED: "border-white text-white", DRAFT: "border-[#292929] text-muted-eff", UNPUBLISHED: "border-[#292929] text-muted-eff" };
  const lbl = { PUBLISHED: "Publié", DRAFT: "Brouillon", UNPUBLISHED: "Dépublié" };
  return <span className={`text-[10px] tracking-widest uppercase border px-2 py-1 ${map[status] || map.DRAFT}`}>{lbl[status] || status}</span>;
};

export const Loading = ({ label = "Chargement..." }) => (
  <div data-testid="panel-loading" className="text-muted-eff text-sm py-8">{label}</div>
);

export const ErrorState = ({ onRetry, label = "Impossible de charger les données. Vérifiez votre connexion ou reconnectez-vous." }) => (
  <div data-testid="panel-error" className="py-10 max-w-md">
    <div className="text-[10px] tracking-[0.3em] uppercase text-muted-eff mb-2">Erreur</div>
    <p className="text-sm text-muted-eff mb-5">{label}</p>
    {onRetry && (
      <button data-testid="panel-retry" onClick={onRetry}
        className="text-xs tracking-widest uppercase px-5 py-2.5 border border-[#292929] hover:border-white transition-colors duration-300">
        Réessayer
      </button>
    )}
  </div>
);

export const Empty = ({ label = "Aucun élément." }) => (
  <div data-testid="panel-empty" className="py-8 text-muted-eff text-sm">{label}</div>
);
